const INLINE_ATTACHMENT_TRACKING_KEY = '__incidentInlineAttachmentTrackingKey';

type InlineTrackedFile = File & {
    [INLINE_ATTACHMENT_TRACKING_KEY]?: string;
};

function createInlineAttachmentTrackingKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `inline-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getInlineAttachmentTrackingKey(file: File): string | undefined {
    return (file as InlineTrackedFile)[INLINE_ATTACHMENT_TRACKING_KEY];
}

export function ensureInlineAttachmentTrackingKey(file: File): string {
    const existingKey = getInlineAttachmentTrackingKey(file);
    if (existingKey) return existingKey;

    const nextKey = createInlineAttachmentTrackingKey();
    Object.defineProperty(file, INLINE_ATTACHMENT_TRACKING_KEY, {
        configurable: true,
        enumerable: false,
        value: nextKey,
        writable: false,
    });
    return nextKey;
}

export function markInlineAttachmentElement(image: HTMLImageElement, file: File) {
    image.dataset.inlineAttachmentKey = ensureInlineAttachmentTrackingKey(file);
    image.dataset.inlineAttachmentFileName = file.name;
    image.alt = file.name;
}

export function removeInlineAttachmentElementsForFile(root: ParentNode | null | undefined, file: File) {
    if (!root) return;

    const trackingKey = getInlineAttachmentTrackingKey(file);
    if (!trackingKey) return;

    const matchingImages = Array.from(
        root.querySelectorAll<HTMLImageElement>('img[data-inline-attachment-key]'),
    ).filter((image) => image.dataset.inlineAttachmentKey === trackingKey);

    matchingImages.forEach((image) => image.remove());
}

export function syncComposerAttachmentsWithEditor(files: File[], root: ParentNode | null | undefined): File[] {
    if (!root) return files;

    const inlineImages = Array.from(root.querySelectorAll<HTMLImageElement>('img[data-inline-attachment-key]'));
    const inlineTrackingKeys = new Set(
        inlineImages
            .map((image) => image.dataset.inlineAttachmentKey?.trim())
            .filter((trackingKey): trackingKey is string => Boolean(trackingKey)),
    );

    return files.filter((file) => {
        const trackingKey = getInlineAttachmentTrackingKey(file);
        if (!trackingKey) {
            return true;
        }

        return inlineTrackingKeys.has(trackingKey);
    });
}
